import { getReservationAnalyticsSession } from './analyticsSession.ts';
import type { ReservationAnalyticsSession } from './analyticsTypes.ts';
import type {
  BookingFlowClientEventInput,
  TimeSelectionSource,
  TransportFailureKind,
  ValidationBlockReason,
} from './bookingFlowAnalyticsTypes.ts';
import {
  createBookingFlowIdentityEngine,
  type BookingFlowAvailabilityResultSnapshot,
  type BookingFlowCanonicalSelection,
  type BookingFlowIdentityEngineOptions,
  type BookingFlowIdentityState,
  type BookingFlowScope,
  type BookingFlowSubmitAttemptResult,
} from './bookingFlowAnalyticsIdentity.ts';
import { buildBookingFlowClientEvent } from './bookingFlowAnalyticsEvents.ts';
import type { BookingFlowAnalyticsValidationIssue } from './bookingFlowAnalyticsValidation.ts';
import { createTrafficScopedBookingFlowId, getBrowserBookingFlowTrafficClass } from './bookingFlowTrafficClass.ts';

export type BookingFlowAnalyticsWriter = (event: BookingFlowClientEventInput) => Promise<void> | void;

export type BookingFlowAnalyticsEmitResult =
  | { emitted: true; event: BookingFlowClientEventInput }
  | { emitted: false; reason: 'unchanged' | 'stale' | 'validation_failed' | 'missing_attempt' | 'exception'; issues?: BookingFlowAnalyticsValidationIssue[] };

type BookingFlowSubmitAttemptEvent = Extract<BookingFlowClientEventInput, { eventType: 'reservation_submit_attempted' }>;

export interface BookingFlowAnalyticsSubmitCorrelation {
  visitorId: string;
  sessionId: string;
  bookingFlowId: string;
  submitAttemptId: string;
}

export type BookingFlowAnalyticsSubmitAttemptEmitResult =
  | { emitted: true; event: BookingFlowSubmitAttemptEvent; submitAttemptId: string; correlation: BookingFlowAnalyticsSubmitCorrelation }
  | { emitted: false; reason: 'unchanged' | 'stale' | 'validation_failed' | 'missing_attempt' | 'exception'; submitAttemptId: string | null; correlation: BookingFlowAnalyticsSubmitCorrelation | null; issues?: BookingFlowAnalyticsValidationIssue[] };

export interface BookingFlowAnalyticsEmitterOptions {
  unitId: string;
  identity: ReturnType<typeof createBookingFlowIdentityEngine>;
  writer?: BookingFlowAnalyticsWriter;
  getSession?: () => ReservationAnalyticsSession;
  createId?: () => string;
  now?: () => string;
  getTimezone?: () => string;
  onDiagnostic?: (event: { type: string; error?: unknown; issues?: BookingFlowAnalyticsValidationIssue[] }) => void;
}

export interface BrowserBookingFlowAnalyticsEmitterOptions {
  unitId: string;
  writer?: BookingFlowAnalyticsWriter;
  getTimezone?: () => string;
  onDiagnostic?: BookingFlowAnalyticsEmitterOptions['onDiagnostic'];
}

const noopWriter: BookingFlowAnalyticsWriter = () => undefined;
const defaultCreateId = () => (typeof crypto !== 'undefined' && 'randomUUID' in crypto
  ? crypto.randomUUID()
  : `${Date.now()}-${Math.random().toString(16).slice(2)}`);
const defaultTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

// Tab-lifetime fallback shared by emitter instances. This preserves logical
// identity across React Strict Mode remounts when sessionStorage is blocked.
const browserIdentityMemory = new Map<string, string>();

const browserSessionStorageAdapter = (): BookingFlowIdentityEngineOptions['storage'] => {
  return {
    getItem: key => {
      try {
        const persisted = typeof window !== 'undefined' ? window.sessionStorage.getItem(key) : null;
        if (persisted !== null) browserIdentityMemory.set(key, persisted);
        return persisted ?? browserIdentityMemory.get(key) ?? null;
      } catch {
        return browserIdentityMemory.get(key) ?? null;
      }
    },
    setItem: (key, value) => {
      browserIdentityMemory.set(key, value);
      try {
        if (typeof window !== 'undefined') window.sessionStorage.setItem(key, value);
      } catch {
        // The emitter remains coherent in memory when browser storage is unavailable.
      }
    },
    removeItem: key => {
      browserIdentityMemory.delete(key);
      try {
        if (typeof window !== 'undefined') window.sessionStorage.removeItem(key);
      } catch {
        // The in-memory identity is still cleared.
      }
    },
  };
};

const hasSelectionChanged = (before: BookingFlowIdentityState | null, after: BookingFlowIdentityState) => !before
  || before.selectionRevision !== after.selectionRevision
  || before.canonicalSelection.requestedDateKey !== after.canonicalSelection.requestedDateKey
  || before.canonicalSelection.headcount !== after.canonicalSelection.headcount
  || before.canonicalSelection.durationMinutes !== after.canonicalSelection.durationMinutes
  || before.canonicalSelection.startTimeBucket !== after.canonicalSelection.startTimeBucket;

const hasAvailabilityChanged = (before: BookingFlowIdentityState | null, after: BookingFlowIdentityState) => !before
  || before.availabilityRevision !== after.availabilityRevision
  || before.lastAvailabilityResult?.selectionRevision !== after.lastAvailabilityResult?.selectionRevision
  || before.lastAvailabilityResult?.requestedDateKey !== after.lastAvailabilityResult?.requestedDateKey
  || before.lastAvailabilityResult?.headcount !== after.lastAvailabilityResult?.headcount
  || before.lastAvailabilityResult?.durationMinutes !== after.lastAvailabilityResult?.durationMinutes
  || before.lastAvailabilityResult?.startTimeBucket !== after.lastAvailabilityResult?.startTimeBucket
  || before.lastAvailabilityResult?.availabilityResult !== after.lastAvailabilityResult?.availabilityResult;

const availabilityMatchesCurrentState = (
  state: BookingFlowIdentityState | null,
  input: BookingFlowAvailabilityResultSnapshot,
) => Boolean(state)
  && state?.selectionRevision === input.selectionRevision
  && state?.canonicalSelection.requestedDateKey === input.requestedDateKey
  && state?.canonicalSelection.headcount === input.headcount
  && state?.canonicalSelection.durationMinutes === input.durationMinutes
  && state?.canonicalSelection.startTimeBucket === input.startTimeBucket;

export const createBookingFlowAnalyticsEmitter = ({
  unitId,
  identity,
  writer = noopWriter,
  getSession = getReservationAnalyticsSession,
  createId = defaultCreateId,
  now = () => new Date().toISOString(),
  getTimezone = defaultTimezone,
  onDiagnostic,
}: BookingFlowAnalyticsEmitterOptions) => {
  const scope = (session: ReservationAnalyticsSession): BookingFlowScope => ({ unitId, sessionId: session.sessionId });

  const emit = <T extends BookingFlowClientEventInput>(
    state: BookingFlowIdentityState,
    eventFactory: (context: { session: ReservationAnalyticsSession }) => Omit<Parameters<typeof buildBookingFlowClientEvent>[1], never>,
    session: ReservationAnalyticsSession,
  ): BookingFlowAnalyticsEmitResult => {
    try {
      const built = buildBookingFlowClientEvent({
        unitId,
        session,
        state,
        eventId: createId(),
        occurredAtClient: now(),
        clientTimezone: getTimezone(),
      }, eventFactory({ session }) as never);
      if (built.valid === false) {
        onDiagnostic?.({ type: 'validation_failed', issues: built.issues });
        return { emitted: false, reason: 'validation_failed', issues: built.issues };
      }
      try {
        void Promise.resolve(writer(built.event)).catch(error => onDiagnostic?.({ type: 'writer_failed', error }));
      } catch (error) {
        onDiagnostic?.({ type: 'writer_failed', error });
      }
      return { emitted: true, event: built.event as T };
    } catch (error) {
      onDiagnostic?.({ type: 'exception', error });
      return { emitted: false, reason: 'exception' };
    }
  };

  const changeSelection = (
    selection: BookingFlowCanonicalSelection,
    build: (state: BookingFlowIdentityState) => Parameters<typeof buildBookingFlowClientEvent>[1],
  ): BookingFlowAnalyticsEmitResult => {
    try {
      const session = getSession();
      const currentScope = scope(session);
      const before = identity.getState(currentScope);
      const state = identity.updateSelection(currentScope, selection);
      if (!hasSelectionChanged(before, state)) return { emitted: false, reason: 'unchanged' };
      return emit(state, () => build(state), session);
    } catch (error) {
      onDiagnostic?.({ type: 'exception', error });
      return { emitted: false, reason: 'exception' };
    }
  };

  const dateSelected = (input: { requestedDateKey: string }) => changeSelection(
    { ...(identity.getState(scope(getSession()))?.canonicalSelection ?? {}), requestedDateKey: input.requestedDateKey },
    state => ({ eventType: 'date_selected', selectionRevision: state.selectionRevision, payload: { requestedDateKey: input.requestedDateKey } }),
  );

  const headcountSelected = (input: { headcount: number }) => changeSelection(
    { ...(identity.getState(scope(getSession()))?.canonicalSelection ?? {}), headcount: input.headcount },
    state => ({ eventType: 'headcount_selected', selectionRevision: state.selectionRevision, payload: { headcount: input.headcount } }),
  );

  const timeSelected = (input: { startTimeBucket: string; source: TimeSelectionSource }) => changeSelection(
    { ...(identity.getState(scope(getSession()))?.canonicalSelection ?? {}), startTimeBucket: input.startTimeBucket },
    state => ({ eventType: 'time_selected', selectionRevision: state.selectionRevision, payload: { startTimeBucket: input.startTimeBucket, source: input.source } }),
  );

  const availabilityResultPresented = (input: BookingFlowAvailabilityResultSnapshot): BookingFlowAnalyticsEmitResult => {
    try {
      const session = getSession();
      const currentScope = scope(session);
      const before = identity.getState(currentScope);
      if (!availabilityMatchesCurrentState(before, input)) return { emitted: false, reason: 'stale' };
      const state = identity.recordAvailabilityResult(currentScope, input);
      if (!hasAvailabilityChanged(before, state) || state.availabilityRevision === undefined) {
        return { emitted: false, reason: 'unchanged' };
      }
      const {
        selectionRevision: _capturedSelectionRevision,
        durationMinutes: _capturedDurationMinutes,
        ...payload
      } = input;
      return emit(state, () => ({
        eventType: 'availability_result_presented',
        selectionRevision: state.selectionRevision,
        availabilityRevision: state.availabilityRevision as number,
        payload,
      }), session);
    } catch (error) {
      onDiagnostic?.({ type: 'exception', error });
      return { emitted: false, reason: 'exception' };
    }
  };

  const contactFormStarted = (): BookingFlowAnalyticsEmitResult => {
    try {
      const session = getSession();
      const currentScope = scope(session);
      const before = identity.getState(currentScope);
      if (before?.contactFormStartedEmitted) return { emitted: false, reason: 'unchanged' };
      const state = identity.markContactFormStarted(currentScope);
      return emit(state, () => ({ eventType: 'contact_form_started', selectionRevision: state.selectionRevision, payload: {} }), session);
    } catch (error) {
      onDiagnostic?.({ type: 'exception', error });
      return { emitted: false, reason: 'exception' };
    }
  };

  const validationBlocked = (input: { reasonCode: ValidationBlockReason }): BookingFlowAnalyticsEmitResult => {
    try {
      const session = getSession();
      const currentScope = scope(session);
      const state = identity.markValidationBlocked(currentScope) ?? identity.getOrCreateFlow(currentScope);
      return emit(state, () => ({ eventType: 'reservation_validation_blocked', selectionRevision: state.selectionRevision, payload: { reasonCode: input.reasonCode } }), session);
    } catch (error) {
      onDiagnostic?.({ type: 'exception', error });
      return { emitted: false, reason: 'exception' };
    }
  };

  const submitAttempted = (): BookingFlowAnalyticsSubmitAttemptEmitResult => {
    try {
      const session = getSession();
      const currentScope = scope(session);
      const attempt: BookingFlowSubmitAttemptResult = identity.getOrCreateSubmitAttempt(currentScope);
      const correlation = {
        visitorId: session.visitorId,
        sessionId: session.sessionId,
        bookingFlowId: attempt.state.bookingFlowId,
        submitAttemptId: attempt.submitAttemptId,
      };
      const result = emit(attempt.state, () => ({
        eventType: 'reservation_submit_attempted',
        selectionRevision: attempt.state.selectionRevision,
        submitAttemptId: attempt.submitAttemptId,
        payload: {},
      }), session);
      if (result.emitted === true) {
        return { emitted: true, event: result.event as BookingFlowSubmitAttemptEvent, submitAttemptId: attempt.submitAttemptId, correlation };
      }
      return { ...result, submitAttemptId: attempt.submitAttemptId, correlation };
    } catch (error) {
      onDiagnostic?.({ type: 'exception', error });
      return { emitted: false, reason: 'exception', submitAttemptId: null, correlation: null };
    }
  };

  const submitTransportFailed = (input: {
    submitAttemptId: string;
    transportFailureKind: TransportFailureKind;
  }): BookingFlowAnalyticsEmitResult => {
    try {
      const session = getSession();
      const state = identity.markSubmitTransportFailed(scope(session), input.submitAttemptId);
      if (!state || state.activeSubmitAttemptId !== input.submitAttemptId) {
        return { emitted: false, reason: 'missing_attempt' };
      }
      return emit(state, () => ({
        eventType: 'reservation_submit_transport_failed',
        submitAttemptId: input.submitAttemptId,
        payload: { transportFailureKind: input.transportFailureKind },
      }), session);
    } catch (error) {
      onDiagnostic?.({ type: 'exception', error });
      return { emitted: false, reason: 'exception' };
    }
  };

  const getCurrentState = (): BookingFlowIdentityState | null => {
    try {
      return identity.getState(scope(getSession()));
    } catch (error) {
      onDiagnostic?.({ type: 'exception', error });
      return null;
    }
  };

  const synchronizeAvailabilitySelection = (selection: BookingFlowCanonicalSelection): BookingFlowIdentityState | null => {
    try {
      const session = getSession();
      return identity.updateSelection(scope(session), selection);
    } catch (error) {
      onDiagnostic?.({ type: 'exception', error });
      return null;
    }
  };

  const markSubmitCreated = (submitAttemptId: string) => {
    const session = getSession();
    return identity.markSubmitCreated(scope(session), submitAttemptId);
  };

  const markSubmitRejected = (submitAttemptId: string) => {
    const session = getSession();
    return identity.markSubmitRejected(scope(session), submitAttemptId);
  };

  return {
    getCurrentState,
    synchronizeAvailabilitySelection,
    dateSelected,
    headcountSelected,
    timeSelected,
    availabilityResultPresented,
    contactFormStarted,
    validationBlocked,
    submitAttempted,
    submitTransportFailed,
    markSubmitCreated,
    markSubmitRejected,
  };
};

export const createBrowserBookingFlowAnalyticsEmitter = (options: BrowserBookingFlowAnalyticsEmitterOptions) => {
  const trafficClass = getBrowserBookingFlowTrafficClass();
  return createBookingFlowAnalyticsEmitter({
    ...options,
    identity: createBookingFlowIdentityEngine({
      storage: browserSessionStorageAdapter(),
      createId: () => createTrafficScopedBookingFlowId(defaultCreateId(), trafficClass),
    }),
  });
};
